'use client';

import useCookiePreferences from '@/hooks/useCookiePreferences';

interface EmbedProps {
  title?: string;
  url?: string;
  embedContent?: string;
  width?: string;
  height?: string;
  sandbox?: boolean;
  allowScripts?: boolean;
  allowSameOrigin?: boolean;
  allowForms?: boolean;
  noReferrer?: boolean;
  disableCookieValidation?: boolean;
}

export default function Embed({
  title,
  url,
  embedContent,
  width = '100%',
  height = '500',
  sandbox = true,
  allowScripts = false,
  allowSameOrigin = false,
  allowForms = false,
  noReferrer = true,
  disableCookieValidation = false,
}: EmbedProps) {
  const sandboxConfig = [
    allowScripts && 'allow-scripts',
    allowSameOrigin && 'allow-same-origin',
    allowForms && 'allow-forms',
  ]
    .filter(Boolean)
    .join(' ');
  const srcProp = !embedContent && url ? { src: url } : null;
  const srcDocProp = embedContent ? { srcDoc: embedContent } : null;
  const sandboxProp = sandbox ? { sandbox: sandboxConfig } : null;
  const referrerValue = noReferrer ? 'no-referrer' : 'strict-origin-when-cross-origin';

  const preferences = useCookiePreferences();
  const cookiesPermitted = preferences.trackingCookies && preferences.recordingCookies;

  if (cookiesPermitted === false && !disableCookieValidation)
    return (
      <div style={{ width: width }} className="bg-background-400 mb-4 p-4">
        <p>
          <strong>Third party content</strong>
          <br />
          This page contains content provided by a third party. We ask for your consent before anything is loaded as the
          third party may be using cookies and other technologies. To view this content, please{' '}
          <button className="font-bold underline" onClick={() => window.postMessage('show_cookie_banner')}>
            update your preferences
          </button>{' '}
          and choose ‘Accept All’.
        </p>
      </div>
    );

  if (cookiesPermitted === true || disableCookieValidation)
    return (
      <div className="mb-4">
        {title && <h3>{title}</h3>}
        <iframe
          {...srcProp}
          {...srcDocProp}
          title={title ?? 'Third party embedded content'}
          width={width}
          height={height}
          referrerPolicy={referrerValue}
          {...sandboxProp}
          loading="lazy"
          scrolling="no"
        />
      </div>
    );

  return null;
}
